<script setup lang="ts">
/**
 * Delete confirmation modal that handles the common "what happens to children?"
 * pattern. When childCount > 0, shows radio options for nullify vs reassign
 * with a conditional select menu.
 */
import { computed, ref, watch } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import { pluralize } from "@/lib/format"
import type { SelectOption } from "@/types/models"

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    loading?: boolean
    childCount: number
    childNoun?: string
    reassignOptions: SelectOption[]
    nullifyLabel?: string
    nullifyDescription?: string
    reassignLabel?: string
    reassignDescription?: string
    reassignPlaceholder?: string
    emptyMessage?: string
  }>(),
  {
    childNoun: "item",
    nullifyLabel: "Remove association",
    nullifyDescription: "Items keep their data but won't have this association",
    reassignLabel: "Reassign to another",
    reassignDescription: "Move all items before deleting",
    reassignPlaceholder: "Choose...",
    emptyMessage: "This action cannot be undone.",
  },
)

const emit = defineEmits<{
  "update:open": [value: boolean]
  confirm: [mode: "nullify" | "reassign", reassignTo: string | null]
}>()

const action = ref<"nullify" | "reassign">("nullify")
const reassignTo = ref<string | null>(null)

watch(
  () => props.open,
  (open) => {
    if (open) {
      action.value = "nullify"
      reassignTo.value = null
    }
  },
)

const disabled = computed(() => action.value === "reassign" && !reassignTo.value)

const childLabel = computed(() => pluralize(props.childCount, props.childNoun))
</script>

<template>
  <DeleteConfirmModal
    :open="open"
    :title="title"
    :loading="loading"
    :disabled="disabled"
    @update:open="emit('update:open', $event)"
    @confirm="emit('confirm', action, reassignTo)"
  >
    <template v-if="childCount === 0">
      <p class="text-muted text-sm">{{ emptyMessage }}</p>
    </template>
    <template v-else>
      <p class="text-muted text-sm">
        <strong class="text-highlighted">{{ childLabel }}</strong>
        {{ childCount === 1 ? "uses" : "use" }} this. What should happen to them?
      </p>

      <div class="mt-4 flex flex-col gap-4">
        <label
          class="flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors"
          :class="action === 'nullify' ? 'border-primary bg-primary/5' : 'border-default'"
        >
          <input
            v-model="action"
            type="radio"
            value="nullify"
            name="delete-action"
            class="mt-0.5"
          />
          <div>
            <div class="text-sm font-medium">{{ nullifyLabel }}</div>
            <div class="text-muted text-xs">{{ nullifyDescription }}</div>
          </div>
        </label>

        <label
          v-if="reassignOptions.length"
          class="flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors"
          :class="action === 'reassign' ? 'border-primary bg-primary/5' : 'border-default'"
        >
          <input
            v-model="action"
            type="radio"
            value="reassign"
            name="delete-action"
            class="mt-0.5"
          />
          <div class="flex-1">
            <div class="text-sm font-medium">{{ reassignLabel }}</div>
            <div class="text-muted text-xs">{{ reassignDescription }}</div>
            <USelectMenu
              v-if="action === 'reassign'"
              :model-value="reassignTo ?? undefined"
              :items="reassignOptions"
              value-key="value"
              :placeholder="reassignPlaceholder"
              class="mt-2"
              @update:model-value="reassignTo = $event"
            />
          </div>
        </label>
      </div>
    </template>
  </DeleteConfirmModal>
</template>
